import type {
  DeficiencyResolveRequest,
  DeficiencyResponse,
  DeficiencyStatusEnum,
  GetListResponseOfDeficiencyResponse,
} from 'src/api/ProactApiClient';
import {inspectionApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

const DEFICIENCY_TAGS = ['Deficiencies'] as const;

export interface GetDeficiencyListParams {
  inspectionId?: number;
  statuses?: DeficiencyStatusEnum[];
  start?: number;
  length?: number;
}

// Newtonsoft sends explicit null; normalize to undefined at the boundary.
const normalizeDeficiencyList = (
  response: GetListResponseOfDeficiencyResponse
): GetListResponseOfDeficiencyResponse => ({
  ...response,
  data: response.data?.map(deficiency => ({
    ...deficiency,
    equipmentTypeName: deficiency.equipmentTypeName ?? undefined,
  })),
});

export const deficiencyApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    // id and inspectionId are the same value; the route nests under the inspection.
    getInspectionDeficiencyList: builder.query<
      GetListResponseOfDeficiencyResponse,
      GetDeficiencyListParams & {id: number}
    >({
      queryFn: clientQuery(
        async ({
          id,
          inspectionId,
          statuses,
          start,
          length,
        }): Promise<GetListResponseOfDeficiencyResponse> =>
          normalizeDeficiencyList(
            await inspectionApiClient.getInspectionDeficiencies(
              id,
              inspectionId,
              statuses,
              start,
              length
            )
          )
      ),
      providesTags: ['Deficiencies'],
    }),
    resolveDeficiency: builder.mutation<
      DeficiencyResponse,
      {id: number; request: DeficiencyResolveRequest}
    >({
      queryFn: clientQuery(({id, request}) =>
        inspectionApiClient.resolveDeficiency(id, request)
      ),
      invalidatesTags: [...DEFICIENCY_TAGS],
    }),
  }),
});

export const {useGetInspectionDeficiencyListQuery, useResolveDeficiencyMutation} =
  deficiencyApi;
